import React, { useState, useEffect } from 'react'
import { connect as connectRedux } from 'react-redux'
import FormsyForm from 'formsy-react'
import compose from 'recompose/compose'
import { connect as connectStaticText } from '../../StaticText/connect'
import { FORM_INPUT_NAME_EMAIL } from '../../../services/form/index'
import { getBoundActions } from '../../../actions/index'
import { EmailInput } from '../../FormInput.v2/index'
import GaiaLogo, { TYPE_TEAL } from '../../GaiaLogo/index'
import Button from '../../Button/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import Sherpa, { TYPE_STATIC_GRAY_DARK } from '../../Sherpa/index'
import {
  handleEmailChange,
  validateEmailFormatOnBlur,
  getForcedEmailError,
  handleUserUpdate,
  BUTTON_STATUS,
  getButtonSettings,
} from '../utils'

/**
 * CreateAccountForm component renders the account creation form,
 * handling various states and actions
 * @param {Object} props - The props
 * @returns {JSX.Element} The rendered account creation form
 */
function CartSinglePageUpdateEmail (props) {
  const {
    user,
    staticText,
    dismissModal,
    getUserEmailAvailability,
  } = props

  const [emailValue, setEmailValue] = useState('')
  const [buttonStatus, setButtonStatus] = useState(BUTTON_STATUS.ACCOUNT_MODIFIED)
  const [updateError, setUpdateError] = useState(false)
  const [formError, setFormError] = useState(false)

  const {
    buttonCopy,
    buttonType,
    buttonDisabled,
    buttonClassName,
    buttonIcon = false,
    buttonSherpa = false,
  } = getButtonSettings(buttonStatus, staticText)
  const emailUnavailable = user.get('emailAvailable') === false
  const processingEmailAvailability = user.get('emailAvailabilityProcessing')
  const isUpdateUserProcessing = user.get('updateUserProcessing')
  const isUserUpdated = user.getIn(['updateUser', 'email'])

  useEffect(() => {
    if (isUpdateUserProcessing) {
      setButtonStatus(BUTTON_STATUS.ACCOUNT_PROCESSING)
    }
    if (
      isUserUpdated === emailValue
      && isUpdateUserProcessing === false
    ) {
      setButtonStatus(BUTTON_STATUS.ACCOUNT_SAVED)
      setTimeout(() => dismissModal(), 500)
    }
  }, [isUpdateUserProcessing, isUserUpdated])

  return (
    <div className="cart-single-page__update-email">
      <div className="cart-single-page__update-email-logo">
        <GaiaLogo type={TYPE_TEAL} />
      </div>
      <div className="cart-single-page__update-email-title">
        {staticText.getIn(['data', 'editAccount'])}
      </div>
      {updateError ? (
        <p className="account-create-form__error-message">
          {staticText.getIn(['data', 'userCreateError'])}
        </p>
      ) : null}
      <FormsyForm
        onValidSubmit={(data) => handleUserUpdate({
          data,
          props,
          setUpdateError,
          isEmailUnavailable: emailUnavailable || processingEmailAvailability,
        })}
        onInvalidSubmit={() => setFormError(true)}
        className="cart-single-page__update-email-form"
        noValidate
      >
        <EmailInput
          className="cart-single-page__update-email-form-email"
          disabled={processingEmailAvailability === true}
          validations={{ isEmail: true }}
          validationErrors={{
            isEmail: staticText.getIn(['data', 'invalidEmail']),
            isEmailUnavailable: staticText.getIn(['data', 'cantUseEmail']),
          }}
          forceError={getForcedEmailError(emailUnavailable)}
          validationTimeout={750}
          label={staticText.getIn(['data', 'emailPlaceholder'])}
          onBlur={(value) => validateEmailFormatOnBlur({
            email: value,
            getUserEmailAvailability,
          })}
          onChange={(value) => handleEmailChange({
            props,
            value,
            emailValue,
            setEmailValue,
          })}
          name={FORM_INPUT_NAME_EMAIL}
          value={emailValue}
          tabIndex="3"
          required
          autocomplete="email"
        />
        <Button
          type={buttonType}
          className={`cart-single-page__update-email-form-submit ${buttonClassName}`}
          tabIndex="10"
          disabled={buttonDisabled}
          submit
        >
          {buttonIcon && <IconV2 className="cart-single-page__create-account-form-submit-icon" type={ICON_TYPES.CHECK} />}
          {buttonSherpa && <Sherpa type={TYPE_STATIC_GRAY_DARK} />}
          {buttonCopy}
        </Button>
        {formError ? (
          <p className="cart-single-page__create-account-form-error">
            {staticText.getIn(['data', 'complete'])}
          </p>
        ) : null}
      </FormsyForm>
    </div>
  )
}

export default compose(
  connectStaticText({ storeKey: 'CartSinglePage' }),
  connectRedux(
    (state) => ({
      auth: state.auth,
      checkout: state.checkout,
      user: state.user,
      userAccount: state.userAccount,
      resolver: state.resolver,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        getUserEmailAvailability: actions.user.getUserEmailAvailability,
        resetUserEmailAvailability: actions.user.resetUserEmailAvailability,
        resetUserChildAccount: actions.user.resetUserChildAccount,
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        updateUser: actions.user.updateUser,
        getUserContactData: actions.user.getUserContactData,
        dismissModal: actions.dialog.dismissModal,
      }
    },
  ),
)(CartSinglePageUpdateEmail)
